"use client";

import React, { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import Button from "@/components/ui/button/Button";
import Label from "@/components/form/Label";
import { useRoles } from "@/hooks/data/useUsers";
import type { AuthUser, Role } from "@/types/domain";

/** Fallback only for the instant before useRoles() resolves — real, current list (built-in +
 * custom, see RolesController) is always fetched live so a newly created custom role shows up
 * here without a redeploy. */
const FALLBACK_ROLES: Role[] = ["admin", "manager", "worker", "customer"];

const inputClass =
  "mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20";

/** Client-side only — no backend "check availability" endpoint exists, so this never claims
 * an email IS available, only flags the obviously-invalid case before submit. Real uniqueness
 * is enforced server-side (AuthApplication.RegisterHandler throws "email-taken") and surfaced
 * as the submit error. */
function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function randomPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
  let out = "";
  for (let i = 0; i < 14; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export interface UserEditValues {
  email: string;
  name: string;
  role: Role;
  extraRoles: Role[];
  active: boolean;
  password: string;
  confirmPassword: string;
}

interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Present in edit mode; absent in create mode. Email/password aren't editable once
   * created — UsersController.Update only accepts Name/Roles/Active, and there's no
   * admin-initiated password reset endpoint (only the public forgot/reset-password flow). */
  initial?: AuthUser;
  onSubmit: (values: UserEditValues) => Promise<void>;
}

const BUILT_IN: string[] = ["admin", "manager", "worker", "customer"];

export default function UserEditModal({ isOpen, onClose, initial, onSubmit }: UserEditModalProps) {
  const isEdit = !!initial;
  const rolesQuery = useRoles();
  const allRoles: Role[] = rolesQuery.data?.map((r) => r.role) ?? FALLBACK_ROLES;
  const customRoles = allRoles.filter((r) => !BUILT_IN.includes(r));
  const [email, setEmail] = useState(initial?.email ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [role, setRole] = useState<Role>(initial?.roles[0] ?? "customer");
  // Built-in roles (admin / manager / worker / customer) are mutually exclusive — one per person, chosen in "Role" above; only CUSTOM roles
  // can be stacked on top. (The old free-for-all let a manager be ticked as "admin" too, and admin passes every access check.)
  const [extraRoles, setExtraRoles] = useState<Role[]>((initial?.roles ?? []).filter((r) => !BUILT_IN.includes(r)));
  const [active, setActive] = useState(initial?.active ?? true);
  const [passwordMode, setPasswordMode] = useState<"auto" | "manual">("auto");
  const [password, setPassword] = useState(randomPassword());
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setEmail(initial?.email ?? "");
      setName(initial?.name ?? "");
      setRole(initial?.roles[0] ?? "customer");
      setExtraRoles(initial?.roles.slice(1) ?? []);
      setActive(initial?.active ?? true);
      setPasswordMode("auto");
      setPassword(randomPassword());
      setConfirmPassword("");
      setError("");
    }
    wasOpen.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initial?.id]);

  const toggleExtraRole = (r: Role) => {
    if (r === role) return; // primary role toggled via the Role select instead
    setExtraRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEdit) {
      if (!isValidEmail(email)) {
        setError("Enter a valid email address");
        return;
      }
      if (!name.trim()) {
        setError("Full name is required");
        return;
      }
      const pwd = passwordMode === "auto" ? password : password;
      if (passwordMode === "manual") {
        if (pwd.length < 10) {
          setError("Password must be at least 10 characters");
          return;
        }
        if (pwd !== confirmPassword) {
          setError("Passwords don't match");
          return;
        }
      }
    } else if (!name.trim()) {
      setError("Full name is required");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onSubmit({ email, name: name.trim(), role, extraRoles, active, password, confirmPassword });
      onClose();
    } catch (err) {
      setError((err as Error)?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="relative w-full overflow-hidden rounded-2xl bg-white p-6 dark:bg-gray-900">
        <div className="mb-4">
          <h4 className="text-xl font-semibold text-gray-800 dark:text-white/90">
            {isEdit ? "Edit user" : "New user"}
          </h4>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isEdit ? "Update name, roles and account status." : "Creates a real account via the registration endpoint."}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="email" required>
                  Email
                </Label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting || isEdit}
                  className={inputClass}
                />
                {isEdit && <p className="mt-1 text-xs text-gray-400">Email can't be changed after creation.</p>}
              </div>
              <div>
                <Label htmlFor="name" required>
                  Full name
                </Label>
                <input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <Label required>Role</Label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                disabled={submitting}
                className={inputClass}
              >
                {allRoles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                One role per person. <b>manager</b> runs the store day to day but has no access to Users, Permissions, Settings, Exports &amp; Reports, System
                Health, Audit Log, AI Ops, Carousel or Image Library; <b>admin</b> can do everything.
              </p>
            </div>

            <div>
              <Label>Additional custom roles</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {customRoles.filter((r) => r !== role).map((r) => (
                  <label
                    key={r}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-200"
                  >
                    <input
                      type="checkbox"
                      checked={extraRoles.includes(r)}
                      disabled={submitting}
                      onChange={() => toggleExtraRole(r)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    {r}
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={active}
                disabled={submitting}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Active {isEdit && !active && <span className="text-xs text-gray-400">(deactivating signs this user out everywhere)</span>}
            </label>

            {!isEdit && (
              <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <Label>Password</Label>
                <div className="mt-1 flex items-center gap-4 text-sm text-gray-700 dark:text-gray-200">
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      checked={passwordMode === "auto"}
                      onChange={() => setPasswordMode("auto")}
                      disabled={submitting}
                    />
                    Auto-generate
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      checked={passwordMode === "manual"}
                      onChange={() => setPasswordMode("manual")}
                      disabled={submitting}
                    />
                    Set manually
                  </label>
                </div>

                {passwordMode === "auto" ? (
                  <div className="mt-2 flex items-center gap-2">
                    <input readOnly value={password} className={`${inputClass} mt-0 font-mono text-sm`} />
                    <button
                      type="button"
                      onClick={() => setPassword(randomPassword())}
                      disabled={submitting}
                      className="shrink-0 rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                      title="Generate a new password"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <input
                      type="password"
                      placeholder="Password (min. 10 characters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={submitting}
                      className={`${inputClass} mt-0`}
                    />
                    <input
                      type="password"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={submitting}
                      className={`${inputClass} mt-0`}
                    />
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-400">
                  A verification email is sent automatically on account creation — there's no separate toggle for it.
                </p>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              {isEdit ? "Save changes" : "Create user"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
